import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { MapPin, ExternalLink, Bookmark, BookmarkCheck, Calendar } from 'lucide-react';
import { useTheme } from '../ui/theme-provider';
import { cn } from '../../lib/utils';
import { formatJobDate, getJobPostedDate, getJobTypeColor } from '../../utils/jobUtils'; // FIXED: Add getJobTypeColor import

const JobDetailsModal = ({ 
  job, 
  isOpen, 
  onClose, 
  savedJobs = [], 
  onSaveJob, 
  onApplyToJob 
}) => {
  const { theme } = useTheme();

  if (!job) return null;

  const jobKey = `${job.source}-${job.id}`;
  const isSaved = savedJobs.includes(jobKey);

  // Enhanced apply handler that ensures proper data is passed
  const handleApply = () => {
    // Enhanced email extraction - check multiple possible fields with contactEmail first
    let emailAddress = '';
    
    // Check for various email field names - prioritize contactEmail
    if (job.contactEmail) {
      emailAddress = job.contactEmail;
    } else if (job.email) {
      emailAddress = job.email;
    } else if (job.practiceEmail) {
      emailAddress = job.practiceEmail;
    } else if (job._originalJob && job._originalJob.contactEmail) {
      emailAddress = job._originalJob.contactEmail;
    } else if (job._originalJob && job._originalJob.email) {
      emailAddress = job._originalJob.email;
    } else if (job._originalJob && job._originalJob.practiceEmail) {
      emailAddress = job._originalJob.practiceEmail;
    } else if (job.originalEmail) {
      emailAddress = job.originalEmail;
    }
    
    // For practice jobs, also check if there's an email in the application instructions
    if (!emailAddress && job.source === 'practice' && job.applicationInstructions) {
      const emailMatch = job.applicationInstructions.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
      if (emailMatch) {
        emailAddress = emailMatch[0];
      }
    }
    
    // FIXED: Enhanced URL extraction for RSS jobs - try multiple fields
    let applicationUrl = '';
    // FIXED: Check for RSS jobs using multiple possible source values
    const isRSSJob = job.source === 'rss' || 
                     job.source === 'hygienist_therapist' || 
                     job.source === 'locum_dentist' || 
                     job.source === 'dental_nurse' ||
                     job.sourceLabel === 'BDJ Jobs';
    
    if (isRSSJob) {
      applicationUrl = job.applicationUrl || job.originalUrl || job.url || job.link || '';
    }
    
    // Create a clean job object with necessary properties for application
    const applicationJob = {
      id: job.id,
      source: job.source,
      title: job.title,
      // Enhanced email handling
      email: emailAddress,
      // FIXED: Use extracted URL for RSS jobs
      applicationUrl: applicationUrl,
      // Include other relevant fields
      practiceInfo: typeof job.practiceInfo === 'string' ? job.practiceInfo : (job.practiceInfoName || ''),
      location: typeof job.location === 'string' ? job.location : '',
      employmentType: job.employmentType || '',
      description: job.description || '',
      applicationInstructions: job.applicationInstructions || ''
    };
    
    // FIXED: Direct handling for RSS jobs - open URL immediately
    if (isRSSJob && applicationJob.applicationUrl) {
      try {
        window.open(applicationJob.applicationUrl, '_blank');
        return; // Exit early after opening URL
      } catch (error) {
        // Fallback silently
      }
    }
    
    // FALLBACK: If parent component doesn't handle email opening, do it directly
    if (applicationJob.source === 'practice' && applicationJob.email) {
      try {
        const subject = encodeURIComponent(`Application for ${applicationJob.title}`);
        const practiceText = applicationJob.practiceInfo ? ` at ${applicationJob.practiceInfo}` : '';
        const body = encodeURIComponent(
          `Dear Hiring Manager,\n\n` +
          `I would like to apply for the ${applicationJob.title} position${practiceText}.\n\n` +
          `Please find my CV attached.\n\n` +
          `Best regards`
        );
        const emailUrl = `mailto:${applicationJob.email}?subject=${subject}&body=${body}`;
        
        window.location.href = emailUrl;
        
        // Also call the parent handler in case it does additional tracking
        if (onApplyToJob) {
          onApplyToJob(applicationJob);
        }
        return;
      } catch (error) {
        // Fallback silently
      }
    }
    
    // Fallback to parent handler
    if (onApplyToJob) {
      onApplyToJob(applicationJob);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={cn(
        "max-w-2xl max-h-[80vh] overflow-y-auto z-[9999]", // FIXED: Increased z-index from z-[200] to z-[9999]
        theme === 'light' ? 'bg-white text-black' : 'bg-[#2A2A3E] border-purple-500/20 text-white'
      )}>
        <DialogHeader>
          <DialogTitle className={cn(
            "text-xl font-bold pr-8",
            theme === 'light' ? 'text-gray-900' : 'text-white'
          )}>
            {job.title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Job Meta */}
          <div className="flex flex-wrap items-center gap-3">
            <Badge 
              variant="secondary" 
              className={cn(
                "text-xs font-medium",
                theme === 'light' ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-800' // FIXED: Better contrast
              )}
            >
              {job.sourceLabel || (() => {
                // FIXED: Add fallback logic for missing sourceLabel
                if (job.source === 'hygienist_therapist' || 
                    job.source === 'locum_dentist' || 
                    job.source === 'dental_nurse' || 
                    job.source === 'rss') {
                  return 'BDJ Jobs';
                }
                if (job.source === 'practice') {
                  return 'Practice';
                }
                if (job.originalUrl && job.originalUrl.includes('bdjjobs.com')) {
                  return 'BDJ Jobs';
                }
                return job.source || 'Unknown';
              })()}
            </Badge>
            {job.employmentType && (
              <Badge 
                variant="outline"
                className="font-medium text-white"
                style={{
                  backgroundColor: getJobTypeColor(job.employmentType),
                  borderColor: getJobTypeColor(job.employmentType)
                }}
              >
                {job.employmentType}
              </Badge>
            )}
            {job.distance && (
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <MapPin className="h-4 w-4 mr-1" />
                {job.distance.toFixed(2)} miles away
              </div>
            )}
          </div>

          {/* Practice Info */}
          {(job.practiceInfo || job.practiceNameAtPosting) && (
            <div>
              <h3 className={cn(
                "font-semibold mb-2",
                theme === 'light' ? 'text-gray-900' : 'text-white'
              )}>
                Practice
              </h3>
              <p className={cn(
                theme === 'light' ? 'text-gray-700' : 'text-gray-300'
              )}>
                {/* ULTRA SAFE practice info rendering */}
                {(() => {
                  // If practiceInfo is a string, use it
                  if (typeof job.practiceInfo === 'string') {
                    return job.practiceInfo;
                  }
                  // If practiceNameAtPosting is a string, use it
                  if (typeof job.practiceNameAtPosting === 'string') {
                    return job.practiceNameAtPosting;
                  }
                  // If practiceInfoName exists (from our sanitization), use it
                  if (job.practiceInfoName && typeof job.practiceInfoName === 'string') {
                    return job.practiceInfoName;
                  }
                  // If practiceInfo is an object, try to extract name safely
                  if (job.practiceInfo && typeof job.practiceInfo === 'object' && job.practiceInfo.name) {
                    return String(job.practiceInfo.name);
                  }
                  // Fallback
                  return 'Practice information not available';
                })()}
              </p>
            </div>
          )}

          {/* Location */}
          {job.location && (
            <div>
              <h3 className={cn(
                "font-semibold mb-2",
                theme === 'light' ? 'text-gray-900' : 'text-white'
              )}>
                Location
              </h3>
              <p className={cn(
                theme === 'light' ? 'text-gray-700' : 'text-gray-300'
              )}>
                {/* ULTRA SAFE location rendering */}
                {(() => {
                  let locationText = '';
                  
                  // Handle location
                  if (typeof job.location === 'string') {
                    locationText = job.location;
                  } else if (typeof job.location === 'object' && job.location.name) {
                    locationText = String(job.location.name);
                  } else {
                    locationText = 'Location not specified';
                  }
                  
                  // Handle postcode
                  let postcodeText = '';
                  if (job.postcode) {
                    if (typeof job.postcode === 'string') {
                      postcodeText = ` (${job.postcode})`;
                    } else if (typeof job.postcode === 'object' && job.postcode.code) {
                      postcodeText = ` (${String(job.postcode.code)})`;
                    } else if (job.practiceInfoPostcode) {
                      postcodeText = ` (${job.practiceInfoPostcode})`;
                    }
                  }
                  
                  return locationText + postcodeText;
                })()}
              </p>
            </div>
          )}

          {/* Salary */}
          {job.salary && (
            <div>
              <h3 className={cn(
                "font-semibold mb-2",
                theme === 'light' ? 'text-gray-900' : 'text-white'
              )}>
                Salary
              </h3>
              <p className={cn(
                theme === 'light' ? 'text-gray-700' : 'text-gray-300'
              )}>
                {job.salary}
              </p>
            </div>
          )}

          {/* Description */}
          {job.description && (
            <div>
              <h3 className={cn(
                "font-semibold mb-2",
                theme === 'light' ? 'text-gray-900' : 'text-white'
              )}>
                Description
              </h3>
              <div className={cn(
                "prose prose-sm max-w-none",
                theme === 'light' ? 'text-gray-700' : 'text-gray-300'
              )}>
                <p className="whitespace-pre-wrap">{job.description}</p>
              </div>
            </div>
          )}

          {/* Application Instructions */}
          {job.applicationInstructions && (
            <div>
              <h3 className={cn(
                "font-semibold mb-2",
                theme === 'light' ? 'text-gray-900' : 'text-white'
              )}>
                How to Apply
              </h3>
              <div className={cn(
                "whitespace-pre-wrap",
                theme === 'light' ? 'text-gray-700' : 'text-gray-300'
              )}>
                {/* FIXED: Clean up application instructions for RSS jobs */}
                {(() => {
                  let instructions = job.applicationInstructions;
                  
                  // FIXED: Check for RSS jobs using multiple possible source values
                  const isRSSJob = job.source === 'rss' || 
                                   job.source === 'hygienist_therapist' || 
                                   job.source === 'locum_dentist' || 
                                   job.source === 'dental_nurse' ||
                                   job.sourceLabel === 'BDJ Jobs' ||
                                   (instructions && instructions.includes('bdjjobs.com'));
                  
                  // For RSS jobs, clean up ugly URLs and make them user-friendly
                  if (isRSSJob) {
                    // Check if it contains a BDJ Jobs URL
                    if (instructions && instructions.includes('bdjjobs.com')) {
                      return (
                        <div className="space-y-2">
                          <p>Click the "Apply Now" button below to apply at BDJ Jobs.</p>
                          <p className="text-sm text-muted-foreground">
                            You will be redirected to the BDJ Jobs website to complete your application.
                          </p>
                        </div>
                      );
                    }
                    
                    // Check for other common job board URLs and clean them up
                    if (instructions && instructions.includes('http')) {
                      // Extract any email addresses from the instructions
                      const emailMatch = instructions.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
                      if (emailMatch) {
                        return `Send your CV to: ${emailMatch[0]}`;
                      }
                      
                      // Generic cleanup for other URLs
                      return (
                        <div className="space-y-2">
                          <p>Click the "Apply Now" button below to apply online.</p>
                          <p className="text-sm text-muted-foreground">
                            You will be redirected to the job board to complete your application.
                          </p>
                        </div>
                      );
                    }
                    
                    // Default message for RSS jobs without URLs
                    return (
                      <div className="space-y-2">
                        <p>Click the "Apply Now" button below to apply at BDJ Jobs.</p>
                        <p className="text-sm text-muted-foreground">
                          You will be redirected to the job board to complete your application.
                        </p>
                      </div>
                    );
                  }
                  
                  // For practice jobs or clean instructions, show as-is
                  return instructions;
                })()}
              </div>
            </div>
          )}

          {/* Posted Date */}
          <div className="flex items-center text-sm text-gray-500 dark:text-gray-500 mb-4">
            <Calendar className="h-4 w-4 mr-2" />
            <span>
              Posted {formatJobDate(getJobPostedDate(job))} {/* FIXED: Use helper function */}
            </span>
          </div>

          {/* Actions - REMOVED save button, only Apply button */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              onClick={handleApply}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Apply Now
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default JobDetailsModal;
